import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== Butler Certifications list (EN) full modernization — Implementer B scope =====
// Design canon: src/pages/about/overview/page.tsx & home (navy #1a2942 / brass #a38e65 / gold #d7b779 / BTN-08 /
//   editorial, not a card wall / no ornamental dividers).
// Factual canon (no fabrication, prices unchanged, EN = no Japanese characters):
//   [S-cur] prior src/pages/en/qualifications/page.tsx (three tiers, comparison, process, ¥ amounts)
//   [S-overview] src/pages/about/overview/page.tsx (BTN08, section grammar)
// Note: EN specialized-course and distance-learning detail routes are NOT registered in the router,
//   so this page links only to the three basic EN detail pages that exist (no dead links).

const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Playfair_Display',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

const tiers = [
  {
    roman: 'I', name: 'Associate Butler', level: 'Beginner', price: '48,000',
    href: '/en/qualifications/associate-butler', img: '/images/generated/jba-fs-en-030-qualifications.webp',
    lead: 'An entry-level certification covering fundamental butler knowledge and etiquette. Perfect for beginners with no prior experience.',
    facts: [['Textbook', '~100 pp.'], ['Study', '2–4 mo.'], ['Prerequisite', 'None']],
    skills: ['Basic Manners & Protocol', 'Table Service Fundamentals', 'Communication Skills'],
  },
  {
    roman: 'II', name: 'Professional Butler', level: 'Intermediate', price: '78,000',
    href: '/en/qualifications/professional-butler', img: '/images/generated/jba-fs-en-031-qualifications.webp',
    lead: 'An intermediate certification for those with broad knowledge and experience, capable of independently providing high-quality service.',
    facts: [['Textbook', '~200 pp.'], ['Study', '4–6 mo.'], ['Prerequisite', 'Associate']],
    skills: ['Advanced Service Techniques', 'Problem-Solving & Judgment', 'Leadership'],
  },
  {
    roman: 'III', name: 'Master Butler', level: 'Advanced', price: '128,000',
    href: '/en/qualifications/master-butler', img: '/images/generated/jba-fs-en-032-qualifications.webp',
    lead: 'The highest certification, for those with exceptional skill and knowledge who mentor other butlers and manage complex situations.',
    facts: [['Textbook', '~300 pp.'], ['Study', '6–8 mo.'], ['Prerequisite', 'Professional']],
    skills: ['Management & Supervision', 'Training & Education', 'Strategic Planning'],
  },
];

const included = [
  'Textbook materials (printed)', 'Online video lessons (1-year access)', 'Practice kit', 'Exam & answer sheets',
  'Exam grading service', 'Certificate & ID card (upon passing)', '3-hour butler salon experience', 'Shipping included',
];

const flow = [
  { n: '1', t: 'Application', d: 'Apply through our online form. Materials are shipped after payment confirmation.' },
  { n: '2', t: 'Materials Arrive & Start', d: 'Receive the textbook, practice kit, video access code, and butler salon voucher.' },
  { n: '3', t: 'Take the Exam', d: 'When ready, complete the exam and return your answer sheet (within 1 year of application).' },
  { n: '4', t: 'Pass / Fail Determination', d: 'Pass with 80 or higher. A certificate and ID card are issued to successful candidates.' },
  { n: '5', t: 'Certification Complete', d: 'Certification is complete upon certificate arrival. It can be listed on your resume (no expiration).' },
];

const notes = [
  'These are private certifications issued by the Japan Butler Association',
  'Certification does not guarantee employment, career advancement, or income',
  'This is a learning programme for knowledge acquisition and self-improvement',
  'Actual employment is subject to each employer’s hiring criteria',
  'Re-examination fee: ¥15,000 (unlimited retakes within 1 year if failed)',
];

function SecHead({ roman, en, children }: { roman?: string; en: string; children: React.ReactNode }) {
  return (
    <div className="mb-9">
      <div className="-mx-5 sm:-mx-8 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 py-[18px]">
        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">{roman ? `${roman}. ` : ''}{en}</span>
      </div>
      <h2 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.45] tracking-[0.01em]">{children}</h2>
    </div>
  );
}

export default function QualificationsEN() {
  useEffect(() => {
    updateSEO({
      title: 'Butler Certification Programs | Three-Tier System | Japan Butler Association',
      description: 'Explore the Japan Butler Association’s butler certification system. Three core tiers—Associate, Professional, and Master Butler—plus specialized qualifications provide systematic pathways from beginner to advanced professional, all through distance learning with a 3-hour salon experience.',
      keywords: 'butler certification, qualification programs, professional training, Associate Professional Master Butler, Japan Butler Association',
      canonical: 'https://butler-association.jp/en/qualifications',
    });
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'Certifications' },
  ];

  return (
    <Layout>
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-fs-en-033-qualifications-v2.webp"
          alt="Butler certifications and qualifications"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_48%,rgba(16,20,26,0.36)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[700px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Butler Certifications</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="font-['Playfair_Display',serif] font-medium text-white text-[clamp(30px,3.6vw,46px)] leading-[1.25] tracking-[0.01em] mb-6">
              <span className="block">Butler Certifications</span>
              <span className="block text-[#d7b779] text-[clamp(20px,2.4vw,30px)] mt-2">Your path to professional excellence</span>
            </h1>
            <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-white/90 leading-[1.9]">
              The Japan Butler Association operates a comprehensive butler certification system. From the foundational Associate Butler through the Professional Butler for experienced practitioners to the industry’s highest Master Butler, our tiers build systematic skill—complemented by specialized qualifications, all through distance learning with a complimentary 3-hour salon experience.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb: placed right after the billboard hero (Yuichi 2026-07-17 note 2). Override shared Breadcrumb's header-clearance top padding via child selector */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-18 lg:py-20 space-y-16 sm:space-y-20">

          <section>
            <SecHead en="Overview">Step by step, <span className="text-[#a38e65]">toward true mastery</span></SecHead>
            <div className="grid lg:grid-cols-[1fr_300px] gap-8 lg:gap-12 items-start">
              <div className="space-y-[18px] font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9]">
                <p>Our certifications are highly regarded by the hotel industry and high-net-worth individuals both in Japan and abroad, serving as an essential step in building a solid career as a butler.</p>
                <p>The heart of the system is the three-tier progression of core certifications. Level-skipping is not permitted: you advance in order from Associate Butler (Beginner) to Professional Butler (Intermediate) and Master Butler (Advanced). Specialized certifications in fields such as fine dining, tea, and business manner are also available.</p>
                <p>Every certification is earned through distance learning, available nationwide, and each includes a complimentary 3-hour butler salon experience (¥30,000 value) at our Odaiba salon.</p>
              </div>
              <div className="lg:pt-1">
                <img
                  src="/images/generated/jba-fs-173-qualifications.webp"
                  alt="The butler salon in Odaiba, a home for learning and experience"
                  className="w-full h-52 lg:h-[240px] object-cover border border-[#a38e65]/30 mb-5"
                />
                <dl className="border-t border-[#e2ddd2] pt-5 space-y-4">
                  <div className="flex items-baseline justify-between">
                    <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[11px] text-[#8f7a56]">Core Tiers</dt>
                    <dd className="font-['Playfair_Display',serif] font-medium text-2xl text-[#1a2942]">3</dd>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-[#e2ddd2] pt-4">
                    <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[11px] text-[#8f7a56]">Pass Score</dt>
                    <dd className="font-['Playfair_Display',serif] font-medium text-2xl text-[#1a2942]">80<span className="text-sm text-[#8f7a56] ml-1">+</span></dd>
                  </div>
                  <div className="flex items-baseline justify-between border-t border-[#e2ddd2] pt-4">
                    <dt className="font-['Playfair_Display',serif] uppercase tracking-[0.06em] text-[11px] text-[#8f7a56]">Bonus</dt>
                    <dd className="font-['Playfair_Display',serif] text-base text-[#1a2942]">3-hr salon</dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>

          {/* Core certifications = three-tier editorial stepper = new variation (a). Origin: overview S5 timeline + S4 numbered blocks */}
          <section>
            <SecHead roman="I" en="Core Certifications">Three core tiers <span className="text-[#a38e65]">of proven skill</span></SecHead>
            <p className="font-['Playfair_Display',serif] text-base text-[#333] leading-[1.9] max-w-3xl mb-10">
              Comprehensive certifications that progressively prove your butler skill. Rise one tier at a time, from beginner to advanced—level-skipping is not permitted. First build the foundation, then practice, then oversight.
            </p>
            <div className="space-y-8">
              {tiers.map((t) => (
                <div key={t.name} className="grid md:grid-cols-[300px_1fr] gap-6 md:gap-9 items-stretch border-t border-[#e2ddd2] pt-8">
                  <div className="relative">
                    <img src={t.img} alt={`${t.name} training`} className="w-full h-52 md:h-full object-cover border border-[#a38e65]/30" />
                    <span className="absolute top-0 left-0 bg-[#1a2942] text-[#d7b779] font-['Playfair_Display',serif] text-xl px-3 py-1.5 tracking-[0.06em]">{t.roman}</span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 mb-3">
                      <div>
                        <h3 className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-xl sm:text-2xl leading-tight">{t.name}</h3>
                        <p className="font-['Playfair_Display',serif] italic text-[13px] text-[#8f7a56] mt-1">{t.level}</p>
                      </div>
                      <div className="text-right">
                        <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.16em] text-[10px] text-[#8f7a56]">Fee</span>
                        <span className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-[26px] sm:text-[28px] leading-none">&yen;{t.price}</span>
                      </div>
                    </div>
                    <p className="font-['Playfair_Display',serif] text-[15px] text-[#4a4638] leading-[1.85] mb-4">{t.lead}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-5">
                      {t.skills.map((s) => (
                        <span key={s} className="flex items-center gap-1.5 font-['Playfair_Display',serif] text-[13px] text-[#1a2942]">
                          <span className="text-[#a38e65] text-[11px]" aria-hidden="true">◆</span>{s}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                      <dl className="flex flex-wrap gap-x-7 gap-y-1">
                        {t.facts.map(([k, v]) => (
                          <div key={k} className="flex items-baseline gap-1.5">
                            <dt className="font-['Playfair_Display',serif] text-[11px] text-[#8f7a56]">{k}</dt>
                            <dd className="font-['Playfair_Display',serif] text-[13px] text-[#1a2942]">{v}</dd>
                          </div>
                        ))}
                      </dl>
                      <a href={t.href} className={`${BTN08_ON_LIGHT} ml-auto !px-8 !py-3 text-[13px]`}>View Details</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <SecHead roman="II" en="Compare">Compare <span className="text-[#a38e65]">the three tiers</span></SecHead>
            <div className="overflow-x-auto border-t-2 border-[#1a2942]">
              <table className="w-full min-w-[560px] border-collapse font-['Playfair_Display',serif]">
                <thead>
                  <tr className="border-b border-[#e2ddd2]">
                    <th className="text-left py-3.5 pr-4 text-sm text-[#8f7a56] font-normal tracking-[0.04em]">Item</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Associate</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Professional</th>
                    <th className="text-center py-3.5 px-3 text-[15px] text-[#1a2942] font-medium">Master</th>
                  </tr>
                </thead>
                <tbody className="text-[14px] text-[#4a4638]">
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">Target</td><td className="py-3.5 px-3 text-center">Beginners</td><td className="py-3.5 px-3 text-center">Experienced</td><td className="py-3.5 px-3 text-center">Instructors</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">Textbook</td><td className="py-3.5 px-3 text-center">~100 pages</td><td className="py-3.5 px-3 text-center">~200 pages</td><td className="py-3.5 px-3 text-center">~300 pages</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">Study Period</td><td className="py-3.5 px-3 text-center">2–4 months</td><td className="py-3.5 px-3 text-center">4–6 months</td><td className="py-3.5 px-3 text-center">6–8 months</td></tr>
                  <tr className="border-b border-[#e2ddd2]"><td className="py-3.5 pr-4 text-[#8f7a56]">Prerequisite</td><td className="py-3.5 px-3 text-center">None</td><td className="py-3.5 px-3 text-center">Associate</td><td className="py-3.5 px-3 text-center">Professional</td></tr>
                  <tr><td className="py-3.5 pr-4 text-[#8f7a56]">Fee</td>
                    <td className="py-3.5 px-3 text-center"><span className="font-medium text-lg text-[#1a2942]">&yen;48,000</span></td>
                    <td className="py-3.5 px-3 text-center"><span className="font-medium text-lg text-[#1a2942]">&yen;78,000</span></td>
                    <td className="py-3.5 px-3 text-center"><span className="font-medium text-lg text-[#1a2942]">&yen;128,000</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <SecHead roman="III" en="What's Included">Everything is <span className="text-[#a38e65]">included in the fee</span></SecHead>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 border-t-2 border-[#1a2942]">
              {included.map((x) => (
                <li key={x} className="flex items-start gap-2.5 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1" aria-hidden="true">◆</span>
                  <span className="font-['Playfair_Display',serif] text-[14px] text-[#1a2942] leading-[1.6]">{x}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 5 steps timeline = new variation (b). Origin: overview S5 timeline */}
          <section>
            <SecHead roman="IV" en="How to Apply">Five steps <span className="text-[#a38e65]">to certification</span></SecHead>
            <ol className="relative border-l border-[#e2ddd2] ml-1 max-w-3xl">
              {flow.map((s) => (
                <li key={s.n} className="relative pl-7 sm:pl-9 pb-8 last:pb-0">
                  <span className="absolute -left-[6px] top-1 w-[11px] h-[11px] rounded-full bg-[#a38e65] ring-4 ring-white"></span>
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="font-['Playfair_Display',serif] text-xl text-[#1a2942]">{s.n}</span>
                    <span className="font-['Playfair_Display',serif] font-medium text-[#1a2942] text-base sm:text-lg">{s.t}</span>
                  </div>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]">{s.d}</p>
                </li>
              ))}
            </ol>
          </section>

          <section>
            <SecHead en="Please Note">Important information</SecHead>
            <ul className="border-t border-[#e2ddd2] max-w-3xl">
              {notes.map((n) => (
                <li key={n} className="flex items-start gap-3 border-b border-[#e2ddd2] py-4">
                  <span className="text-[#a38e65] text-[13px] leading-none pt-1.5" aria-hidden="true">◆</span>
                  <span className="font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.8]">{n}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">Next Steps</span>
              <h2 className="font-['Playfair_Display',serif] font-medium text-[#f8f6f0] text-2xl sm:text-3xl leading-[1.4] mb-5">Find your perfect certification</h2>
              <p className="font-['Playfair_Display',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.9] max-w-3xl mb-8">
                We will recommend the best certification path for your career goals and interests. Explore our butler services, or reach out with any question.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/en/butler-services/" className={BTN08_ON_NAVY}>Explore Butler Services</a>
                <a href="/en/contact" className={BTN08_ON_NAVY}>Contact Us</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
